import React, { useMemo, useState } from 'react';
import { ChevronDown, Check, Plus, Search, Loader } from 'lucide-react';
import BottomSheet from './BottomSheet';

/**
 * Mobile-friendly select: a field that opens a searchable sheet. When the
 * search matches nothing exactly, offers "+ Create '<query>'".
 *
 * options: [{ id, label, sublabel? }]
 * onCreate(query): async, should resolve to the new option's id (or null to cancel)
 */
function SearchPicker({
  label,
  value,
  options,
  onSelect,
  onCreate,
  placeholder = 'Select…',
  noneLabel = null,
  disabled = false,
  createLabel = 'Create',
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [creating, setCreating] = useState(false);

  const selected = options.find(o => String(o.id) === String(value));
  const q = query.trim().toLowerCase();
  const filtered = useMemo(
    () => (q ? options.filter(o => `${o.label} ${o.sublabel || ''}`.toLowerCase().includes(q)) : options),
    [options, q]
  );
  const exactMatch = options.some(o => o.label.toLowerCase() === q);

  const close = () => {
    setOpen(false);
    setQuery('');
  };

  const choose = (id) => {
    onSelect(id);
    close();
  };

  const handleCreate = async () => {
    setCreating(true);
    try {
      const newId = await onCreate(query.trim());
      if (newId != null) choose(newId);
    } finally {
      setCreating(false);
    }
  };

  return (
    <>
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen(true)}
        className="w-full flex items-center justify-between gap-2 rounded-xl border border-gray-300 bg-white px-3 py-3 text-left disabled:bg-gray-50 disabled:text-gray-400"
      >
        <span className="min-w-0">
          <span className="block text-xs text-gray-500">{label}</span>
          <span className={`block truncate ${selected ? 'text-gray-900' : 'text-gray-400'}`}>
            {selected ? selected.label : (value === '' && noneLabel) ? noneLabel : placeholder}
          </span>
          {selected?.sublabel && <span className="block text-xs text-gray-500 truncate">{selected.sublabel}</span>}
        </span>
        <ChevronDown className="w-5 h-5 text-gray-400 flex-shrink-0" />
      </button>

      <BottomSheet open={open} onClose={close} title={label} fullHeight>
        <div className="sticky top-0 bg-white pb-3 -mx-1 px-1">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
            <input
              type="search"
              value={query}
              onChange={e => setQuery(e.target.value)}
              placeholder="Search or type a new name"
              className="w-full pl-10 pr-3 py-3 rounded-xl border border-gray-300 text-base focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
          </div>
        </div>

        <div className="divide-y divide-gray-100">
          {onCreate && q && !exactMatch && (
            <button
              type="button"
              onClick={handleCreate}
              disabled={creating}
              className="w-full flex items-center gap-3 py-3 text-left text-blue-600 font-medium disabled:opacity-60"
            >
              {creating ? <Loader className="w-5 h-5 animate-spin" /> : <Plus className="w-5 h-5" />}
              {createLabel} “{query.trim()}”
            </button>
          )}
          {noneLabel && !q && (
            <button type="button" onClick={() => choose('')} className="w-full flex items-center justify-between py-3 text-left">
              <span className="text-gray-700">{noneLabel}</span>
              {value === '' && <Check className="w-5 h-5 text-blue-600" />}
            </button>
          )}
          {filtered.map(o => (
            <button
              key={o.id}
              type="button"
              onClick={() => choose(o.id)}
              className="w-full flex items-center justify-between gap-3 py-3 text-left"
            >
              <span className="min-w-0">
                <span className="block text-gray-900 truncate">{o.label}</span>
                {o.sublabel && <span className="block text-sm text-gray-500 truncate">{o.sublabel}</span>}
              </span>
              {String(o.id) === String(value) && <Check className="w-5 h-5 text-blue-600 flex-shrink-0" />}
            </button>
          ))}
          {filtered.length === 0 && !onCreate && (
            <p className="py-6 text-center text-sm text-gray-500">No matches</p>
          )}
        </div>
      </BottomSheet>
    </>
  );
}

export default SearchPicker;
